// @flow
import * as React from 'react';
import Select, { components } from 'react-select';
import DoneIcon from '@material-ui/icons/Done';
import styled, { withTheme } from 'styled-components';

const OptionContainer = styled.div`
  display: flex;
  align-items: center;
`;

const SelectedIcon = styled(DoneIcon)`
  width: 18px;
  height: 18px;
  padding-right: 4px;
`;

export interface DropdownItem<AcceptedValue> {
  label: string;
  value: AcceptedValue;
}

function DropdownIndicator(props) {
  return (
    components.DropdownIndicator && (
      <components.DropdownIndicator {...props}>
        <i className="material-icons">arrow_drop_down</i>
      </components.DropdownIndicator>
    )
  );
}

function Option(props) {
  return (
    components.Option && (
      <components.Option {...props}>
        <OptionContainer>
          {props.data.value === props.selectProps.value.value && <SelectedIcon /> }
          {props.data.label}
        </OptionContainer>
      </components.Option>
    )
  );
}

interface Props<AcceptedValue> {
  defaultValue?: AcceptedValue;
  items: DropdownItem<AcceptedValue>[];
  onChange: (value: AcceptedValue) => void;
  onOpen: () => void | void;
  theme: any;
}

function Dropdown<T>(props: Props<T>) {
  function onChange(item: DropdownItem<T>) {
    props.onChange(item.value);
  }

  function onOpen() {
    props.onOpen && props.onOpen();
  }

  function findDefaultValue() {
    return props.defaultValue && props.items.find(item => item.value === props.defaultValue);
  }

  return (
    <Select
      components={{ DropdownIndicator, Option }}
      defaultValue={findDefaultValue()}
      isClearable={false}
      isDisabled={false}
      isLoading={false}
      isMulti={false}
      isSearchable={false}
      onChange={onChange}
      onMenuOpen={onOpen}
      options={props.items}
      styles={getStyles(props.theme)}
    />
  );
}

function getStyles(theme: Object) {
  return {
    container: (styles, state) => {
      return {
        ...styles,
        height: theme.V3.metrics.form.input.height,
      };
    },
    control: (styles, state) => {
      const borderColor = state.isFocused ? theme.V3.colors.primary.normal : theme.V3.colors.form.border;

      return {
        ...styles,
        height: '100%',
        minHeight: theme.V3.metrics.form.input.height,
        backgroundColor: theme.V3.colors.surface,
        borderRadius: theme.V3.metrics.borderRadius,
        boxShadow: 'none',
        borderColor,
        '&:hover': {
          borderColor,
        },
      };
    },
    menu: (styles, state) => {
      return {
        ...styles,
        backgroundColor: theme.V3.colors.surface,
        borderRadius: theme.V3.metrics.borderRadius,
        boxShadow: theme.V3.boxShadow,
        marginTop: 0,
      };
    },
    option: (styles, state) => {
      return {
        ...styles,
        backgroundColor: theme.V3.colors.surface,
        color: state.isSelected ? theme.V3.colors.primary.normal : theme.V3.colors.text.surface,
        '&:hover': {
          backgroundColor: theme.V3.colors.form.dropdownHover,
        },
        '&:active': {
          backgroundColor: theme.V3.colors.form.dropdownHover,
        },
      };
    },
    indicatorSeparator: styles => ({ display: 'none' }),
    dropdownIndicator: (styles, state) => {
      const color = state.isFocused ? theme.V3.colors.primary.normal : theme.V3.colors.text.surface;
      return {
        ...styles,
        backgroundColor: theme.V3.colors.surface,
        padding: 0,
        paddingLeft: theme.V3.metrics.margin.tiny,
        paddingRight: theme.V3.metrics.margin.tiny,
        color,
        '&:hover': {
          color,
        },
      };
    },
  };
}

export default withTheme(Dropdown);



// WEBPACK FOOTER //
// ./src/common/components/ui/Dropdown.js